import React, { useState, useEffect, useRef } from "react";
import "./Category.css";
import { useNavigate, useParams } from "react-router-dom";
import zmLogo from "./images/LOGO_ZM.png";
import phoneIcon from "./images/phone-icon.png";
import docIcon from "./images/doc-icon.png";
import podcastIcon from "./images/podcast-icon.png";
import videoIcon from "./images/video-icon.png";
import facebookIcon from "./images/facebook-icon.png";
import instagramIcon from "./images/instagram-icon.png";
import twitterIcon from "./images/twitter-icon.png";

export default function Category() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeMobileSection, setActiveMobileSection] = useState(null);

  const navItemRefs      = useRef({});
  const [hoveredNavKey, setHoveredNavKey] = useState(null);
  const dropdownTimeout  = useRef(null);

  const [categoryData, setCategoryData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(12);

  const API_BASE = process.env.REACT_APP_API_BASE || "https://backend-zonamicroondas.vercel.app/api";
  const NAV_MAX  = 6;
  const MAS_KEY  = '__mas__';

  const closeMobileNav = () => { setMobileNavOpen(false); setActiveMobileSection(null); };

  const showDrop  = (key) => { if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current); setHoveredNavKey(key); };
  const hideDrop  = ()    => { dropdownTimeout.current = setTimeout(() => setHoveredNavKey(null), 160); };
  const keepDrop  = ()    => { if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current); };
  const getDropPos = (key) => {
    const el = navItemRefs.current[key];
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { top: r.bottom, left: r.left };
  };

  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeMobileNav(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleNewsNavigation = (article) => {
    if (!article) return;
    const { canonical_slug, id } = article;
    const categorySlug = article.category_slug || slug || "";
    if (canonical_slug) {
      navigate(categorySlug
        ? `/${encodeURIComponent(categorySlug)}/articulos/${encodeURIComponent(canonical_slug)}`
        : `/news/${encodeURIComponent(canonical_slug)}`
      );
    } else if (id) {
      navigate(`/news/${encodeURIComponent(id)}`);
    }
    closeMobileNav();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Fecha no disponible";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    const months = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const categoriesResponse = await fetch(`${API_BASE}/categories`);
        const categoriesData = await categoriesResponse.json();

        let catsArray = [];
        if (categoriesData && Array.isArray(categoriesData.data)) catsArray = categoriesData.data;
        else if (categoriesData && Array.isArray(categoriesData.categories)) catsArray = categoriesData.categories;
        else if (Array.isArray(categoriesData)) catsArray = categoriesData;

        if (!mounted) return;
        setCategories(catsArray);

        const currentCategory = catsArray.find(cat => String(cat.slug) === String(slug));
        if (!currentCategory) {
          setError("Categoría no encontrada");
          setLoading(false);
          return;
        }

        const newsResponse = await fetch(
          `${API_BASE}/news?status=published&category_id=${currentCategory.id}&limit=200`
        );
        const newsData = await newsResponse.json();
        if (!mounted) return;

        if (newsData && newsData.success && Array.isArray(newsData.data) && newsData.data.length > 0) {
          const mappedNews = newsData.data.map(article => ({
            id: article.id,
            title: article.title,
            subtitle: article.subtitle || article.summary || "",
            date: article.published_at || article.created_at,
            isFeatured: article.is_featured === 1 || article.is_featured === true || String(article.is_featured) === '1',
            image: article.image_url || (article.news_images && article.news_images[0] && article.news_images[0].url) || "/images/default-news.jpg",
            canonical_slug: article.canonical_slug,
            category_slug: article.category_slug || currentCategory.slug
          }));

          mappedNews.sort((a, b) => new Date(b.date) - new Date(a.date));

          const featuredNews = mappedNews.filter(n => n.isFeatured);
          const regularNews = mappedNews.filter(n => !n.isFeatured);

          const mainNews = featuredNews.length > 0 ? featuredNews[0] : (mappedNews.length > 0 ? mappedNews[0] : null);
          const restNews = featuredNews.length > 0
            ? [...featuredNews.slice(1), ...regularNews].sort((a, b) => new Date(b.date) - new Date(a.date))
            : mappedNews.slice(1);

          setCategoryData({ categoryName: currentCategory.name, featuredNews: mainNews, regularNews: restNews });
        } else {
          setCategoryData({ categoryName: currentCategory.name, featuredNews: null, regularNews: [] });
        }

        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        setError("Error al cargar la categoría.");
        setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, [slug, API_BASE]);

  const loadMore = () => setVisibleCount(prev => prev + 12);

  const isNullLike = (v) => {
    if (v === null || v === undefined) return true;
    if (v === 0) return true;
    if (typeof v === 'string' && v.trim() === '') return true;
    if (String(v).toLowerCase() === 'null') return true;
    return false;
  };

  const getChildCategories = (parentId) =>
    categories.filter(cat => !isNullLike(cat.parent_id) && String(cat.parent_id) === String(parentId));

  const rootCategories = categories.filter(cat => isNullLike(cat.parent_id));
  const parentIds = new Set(categories.map(cat => cat.parent_id));
  const referencedParents = categories.filter(cat => parentIds.has(cat.id));
  const baseParentCategories = rootCategories.length > 0
    ? rootCategories
    : (referencedParents.length > 0 ? referencedParents : categories);

  const sortedParentCategories = [...baseParentCategories].sort((a, b) => {
    const aHas = getChildCategories(a.id).length > 0;
    const bHas = getChildCategories(b.id).length > 0;
    if (aHas !== bHas) return aHas ? -1 : 1;
    const aPos = Number(a.position) || 0;
    const bPos = Number(b.position) || 0;
    if (aPos !== bPos) return aPos - bPos;
    return (a.name || "").localeCompare((b.name || ""), undefined, { sensitivity: 'base' });
  });

  const visibleNavCats  = sortedParentCategories.slice(0, NAV_MAX);
  const overflowNavCats = sortedParentCategories.slice(NAV_MAX);

  if (loading) return (
    <div className="category-pro">
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Cargando noticias...</p>
      </div>
    </div>
  );
  if (error) return (
    <div className="category-pro">
      <div className="error-screen">
        <p>{error}</p>
        <button onClick={() => navigate("/")}>Volver al inicio</button>
      </div>
    </div>
  );
  if (!categoryData) return null;

  const heroNews = categoryData.featuredNews;
  const displayedNews = Array.isArray(categoryData.regularNews)
    ? categoryData.regularNews.slice(0, visibleCount)
    : [];

  return (
    <div className="category-pro">

      {/* ══ HEADER + NAVBAR — wrapper sticky único ════════════════════ */}
      <div className="site-top">
        <header className="header-pro">
          <div className="header-pro-inner">

            <button className="header-logo-btn" onClick={() => navigate("/")} aria-label="Ir al inicio">
              <img src={zmLogo} alt="Zona Microondas" />
            </button>

            <div className="header-brand">
              <span className="header-brand-title">Querétaro</span>
              <span className="header-brand-sub">LA NOTICIA AL MOMENTO</span>
            </div>

            <div className="header-right">
              <div className="header-socials">
                <a href="https://www.facebook.com/ZonaMicroondas" className="h-soc"
                   target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <img src={facebookIcon} alt="" />
                </a>
                <a href="https://www.instagram.com/zonamicroondasqro" className="h-soc"
                   target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <img src={instagramIcon} alt="" />
                </a>
                <a href="https://twitter.com" className="h-soc"
                   target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <img src={twitterIcon} alt="" />
                </a>
              </div>
              <div className="header-media">
                <button className="h-media" title="Documentos"><img src={docIcon} alt="Documentos" /></button>
                <button className="h-media" title="Podcast"><img src={podcastIcon} alt="Podcast" /></button>
                <button className="h-media" title="Videos"><img src={videoIcon} alt="Videos" /></button>
                <button className="h-media" title="Contacto"><img src={phoneIcon} alt="Contacto" /></button>
              </div>
            </div>

          </div>
        </header>

        {/* ══ NAVBAR — categorías horizontales ═════════════════════════ */}
        <nav className="navbar-pro" aria-label="Secciones principales">
          <div className="navbar-inner">

            <button
              className={`navbar-mob-toggle ${mobileNavOpen ? 'is-open' : ''}`}
              onClick={() => setMobileNavOpen(p => !p)}
              aria-label={mobileNavOpen ? "Cerrar menú" : "Ver secciones"}
              aria-expanded={mobileNavOpen}
            >
              <div className="toggle-bars">
                <span></span><span></span><span></span>
              </div>
              <span className="toggle-label">{mobileNavOpen ? 'CERRAR' : 'SECCIONES'}</span>
            </button>

            <div className={`navbar-cats ${mobileNavOpen ? 'is-open' : ''}`}>

              {mobileNavOpen ? (
                /* ── DRAWER MÓVIL — drill-down ── */
                activeMobileSection ? (
                  /* Nivel 2: subcategorías */
                  <>
                    <button className="nav-back-btn" onClick={() => setActiveMobileSection(null)}>
                      <svg width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true">
                        <path d="M7 1L1 6L7 11" stroke="currentColor" strokeWidth="2.2"
                              strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {activeMobileSection.name}
                    </button>
                    {getChildCategories(activeMobileSection.id).map(child => (
                      <button key={String(child.id)} className="nav-link"
                        onClick={() => { navigate(`/categoria/${child.slug}`); closeMobileNav(); }}>
                        {child.name}
                      </button>
                    ))}
                    <button className="nav-link nav-see-all-mob"
                      onClick={() => { navigate(`/categoria/${activeMobileSection.slug}`); closeMobileNav(); }}>
                      Ver todo →
                    </button>
                  </>
                ) : (
                  /* Nivel 1: lista principal */
                  <>
                    <button className="nav-link nav-home" onClick={() => { navigate("/"); closeMobileNav(); }}>
                      Inicio
                    </button>
                    {sortedParentCategories.map(parent => {
                      const children = getChildCategories(parent.id);
                      const key = String(parent.id);
                      return (
                        <div key={key} className="nav-item">
                          <button
                            className="nav-link"
                            onClick={() => {
                              if (children.length > 0) {
                                setActiveMobileSection(parent);
                              } else {
                                navigate(`/categoria/${parent.slug}`);
                                closeMobileNav();
                              }
                            }}
                          >
                            {parent.name}
                            {children.length > 0 && (
                              <svg className="nav-chevron" width="10" height="6"
                                   viewBox="0 0 10 6" fill="none" aria-hidden="true">
                                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2"
                                      strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </>
                )
              ) : (
                /* ── NAVBAR DESKTOP — sin cambios ── */
                <>
                  <button className="nav-link nav-home" onClick={() => navigate("/")}>
                    Inicio
                  </button>

                  {visibleNavCats.map(parent => {
                    const children  = getChildCategories(parent.id);
                    const key       = String(parent.id);
                    const isHovered = hoveredNavKey === key;
                    return (
                      <div
                        key={key}
                        className={`nav-item ${isHovered ? 'is-hovered' : ''}`}
                        ref={el => { navItemRefs.current[key] = el; }}
                        onMouseEnter={() => { if (children.length > 0) showDrop(key); }}
                        onMouseLeave={hideDrop}
                      >
                        <button
                          className="nav-link"
                          aria-haspopup={children.length > 0 || undefined}
                          aria-expanded={isHovered || undefined}
                          onClick={() => { navigate(`/categoria/${parent.slug}`); }}
                        >
                          {parent.name}
                          {children.length > 0 && (
                            <svg className={`nav-chevron ${isHovered ? 'rotated' : ''}`}
                                 width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                              <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                        {children.length > 0 && isHovered && (() => {
                          const pos = getDropPos(key);
                          if (!pos) return null;
                          return (
                            <div className="nav-subdrop-fixed"
                              style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
                              onMouseEnter={keepDrop} onMouseLeave={hideDrop}>
                              {children.map(child => (
                                <button key={String(child.id)} className="nav-subdrop-item"
                                  onClick={() => { navigate(`/categoria/${child.slug}`); }}>
                                  {child.name}
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}

                  {overflowNavCats.length > 0 && (() => {
                    const isHovered = hoveredNavKey === MAS_KEY;
                    return (
                      <div
                        className={`nav-item ${isHovered ? 'is-hovered' : ''}`}
                        ref={el => { navItemRefs.current[MAS_KEY] = el; }}
                        onMouseEnter={() => showDrop(MAS_KEY)}
                        onMouseLeave={hideDrop}
                      >
                        <button className="nav-link nav-mas" aria-haspopup="true"
                          aria-expanded={isHovered || undefined}>
                          Más
                          <svg className={`nav-chevron ${isHovered ? 'rotated' : ''}`}
                               width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2"
                                  strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        {isHovered && (() => {
                          const pos = getDropPos(MAS_KEY);
                          if (!pos) return null;
                          return (
                            <div className="nav-subdrop-fixed"
                              style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
                              onMouseEnter={keepDrop} onMouseLeave={hideDrop}>
                              {overflowNavCats.map(cat => (
                                <button key={String(cat.id)} className="nav-subdrop-item"
                                  onClick={() => { navigate(`/categoria/${cat.slug}`); }}>
                                  {cat.name}
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            <span className="navbar-handle">@ZONAMICROONDAS</span>
          </div>

          {mobileNavOpen && (
            <div className="navbar-overlay" onClick={closeMobileNav} aria-hidden="true"></div>
          )}
        </nav>
      </div>{/* /site-top */}

      {/* ── MAIN ── */}
      <main className="cat-main">

        {/* Breadcrumb */}
        <div className="breadcrumb-pro">
          <button className="breadcrumb-home" onClick={() => navigate("/")}>Inicio</button>
          <span className="breadcrumb-sep">›</span>
          <span className="breadcrumb-current">{categoryData.categoryName}</span>
        </div>

        {/* Título de sección */}
        <div className="cat-title-bar">
          <div className="cat-title-accent"></div>
          <h1 className="cat-page-title">{categoryData.categoryName}</h1>
        </div>

        {/* ── HERO GIGANTE ── */}
        {heroNews && (
          <section
            className="hero-gigante"
            style={{ backgroundImage: `url(${heroNews.image})` }}
            onClick={() => handleNewsNavigation(heroNews)}
            role="article"
            aria-label={`Noticia principal: ${heroNews.title}`}
          >
            <div className="hero-overlay">
              <div className="hero-content">
                <span className="hero-badge">
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" aria-hidden="true"><circle cx="4" cy="4" r="4"/></svg>
                  {formatDate(heroNews.date)}
                </span>
                <h2 className="hero-title">{heroNews.title}</h2>
                {heroNews.subtitle && (
                  <p className="hero-subtitle">{heroNews.subtitle}</p>
                )}
                <button
                  className="hero-cta"
                  onClick={(e) => { e.stopPropagation(); handleNewsNavigation(heroNews); }}
                >
                  Leer artículo completo
                  <span className="hero-cta-arrow" aria-hidden="true">→</span>
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ── GRID ELITE ── */}
        {displayedNews.length > 0 && (
          <section className="grid-elite-section">
            <div className="grid-section-header">
              <div className="grid-title-accent"></div>
              <h2 className="grid-title">Más de {categoryData.categoryName}</h2>
            </div>

            <div className="grid-elite">
              {displayedNews.map((article, i) => (
                <article
                  key={article.id}
                  className="card-pro"
                  style={{ animationDelay: `${Math.min(i * 0.07, 0.42)}s` }}
                  onClick={() => handleNewsNavigation(article)}
                  aria-label={article.title}
                >
                  <div className="card-img-wrap">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="card-img"
                      loading="lazy"
                    />
                    <span className="card-category-badge">{categoryData.categoryName}</span>
                  </div>
                  <div className="card-body">
                    <time className="card-date" dateTime={article.date}>
                      {formatDate(article.date)}
                    </time>
                    <h3 className="card-title">{article.title}</h3>
                    {article.subtitle && (
                      <p className="card-excerpt">{article.subtitle}</p>
                    )}
                    <button
                      className="card-cta"
                      onClick={(e) => { e.stopPropagation(); handleNewsNavigation(article); }}
                    >
                      Leer más <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Estado vacío */}
        {!heroNews && displayedNews.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon" aria-hidden="true">📰</div>
            <p>No hay noticias disponibles en esta categoría.</p>
            <button onClick={() => navigate("/")}>Volver al inicio</button>
          </div>
        )}

        {/* Cargar más */}
        {Array.isArray(categoryData.regularNews) && categoryData.regularNews.length > visibleCount && (
          <div className="load-more-wrap">
            <button className="btn-load-more-pro" onClick={loadMore}>
              Ver más noticias
              <span className="load-more-count">
                +{categoryData.regularNews.length - visibleCount}
              </span>
            </button>
          </div>
        )}
      </main>

      {/* ── FOOTER ── */}
      <footer className="footer-pro">
        <p className="footer-pro-text">
          Copyright © Todos los derechos reservados | ZONAMICROONDAS. De no existir previa autorización,
          queda expresamente prohibida la publicación, retransmisión, edición y cualquier otro uso de los contenidos.
        </p>
      </footer>
    </div>
  );
}
